import * as React from 'react';
import { difference } from 'lodash';
import pluralize from 'pluralize';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import { CSVParser, CSVUploadLoadingState, UploadTextAndButton } from 'components/File';
import { ComposableTable, ColumnHeader, getTextCellGenerator } from 'components/ComposableTable';
import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import DropZoneModal from 'components/DropZoneModal';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import { ParsedResults } from 'components/File/CSVParser';

import './styles/CSVPreviewWidget.scss';

export type Props = {
  validColumnNames?: string[];
  // if the parent component needs the parsed results,
  // you can pass in a callback function
  onParse?: (data: ParsedResults) => void;
  // callback specifically called when parsing is successful
  onSuccess?: () => void;
  // callback specifically called when parsing is unsuccessful
  onError?: () => void;

  // Drag 'n drop props
  connectDropTarget?: Function;
  isDraggingFile?: boolean;
};

type State = {
  csvFromDragDrop?: File;
};

class CSVPreviewWidget extends React.Component<Props, State> {
  state = {
    csvFromDragDrop: undefined,
  };

  renderDropzoneModal = () => {
    const { isDraggingFile } = this.props;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal titleDrop="Drop a CSV file here" isHovering />
      </div>
    );
  };

  renderResultsHeader = (csvName: string, numberOfRows: number, pick: () => void) => {
    return (
      <div className="CSVPreviewWidget-results-header">
        <MediaObject figure="right" center>
          <Columns2>
            <React.Fragment>
              <Row size="small">
                <Text weight="semibold" element="div">
                  Test Data
                </Text>
              </Row>
              <Text weight="semibold" element="div">
                Rows Detected
              </Text>
            </React.Fragment>
            <React.Fragment>
              <Row size="small">
                <Text element="div">{csvName}</Text>
              </Row>
              <Text element="div">{numberOfRows}</Text>
            </React.Fragment>
          </Columns2>
          <Button level="secondary" onClick={pick}>
            Upload new file
          </Button>
        </MediaObject>
      </div>
    );
  };

  renderResultsTable = (parsedResults: ParsedResults) => {
    return (
      <div className="CSVPreviewWidget-results-table">
        <ComposableTable
          fixedHeader
          spreadsheet
          keyField={parsedResults.columnNames[0]}
          columnHeaders={getColumnHeaders(parsedResults.columnNames)}
          columnGenerators={getColumnGenerators(parsedResults.columnNames)}
          rows={parsedResults.parsedRowData}
        />
      </div>
    );
  };

  renderUploadNullOrError = (pick: () => void, errorText?: string) => {
    const { connectDropTarget } = this.props;
    if (connectDropTarget) {
      return connectDropTarget(
        <div>
          {this.renderDropzoneModal()}
          <UploadTextAndButton size="large" pick={pick} error={errorText} />
        </div>
      );
    }
    return <UploadTextAndButton size="large" pick={pick} error={errorText} />;
  };

  renderUpload = (pick: () => void, loading: boolean, errorText?: string) => {
    return (
      <div className="CSVPreviewWidget-dropzone">
        {loading ? (
          <CSVUploadLoadingState text="Processing your CSV file..." />
        ) : (
          this.renderUploadNullOrError(pick, errorText)
        )}
      </div>
    );
  };

  render() {
    return (
      <CSVParser
        onCSVParse={this.props.onParse}
        csvFromDragDrop={this.state.csvFromDragDrop}
        onSuccess={this.props.onSuccess}
        onError={this.props.onError}
      >
        {({ pick, csv, parsedResults, parseErrors, loading }) => {
          if (parsedResults && csv) {
            if (this.props.validColumnNames) {
              // if `validColumnNames` is passed in, make sure that the parsed results
              // contain columns for all of the valid column names
              const missingValidColumns = difference(
                this.props.validColumnNames,
                parsedResults.columnNames
              );
              if (missingValidColumns.length > 0) {
                return this.renderUpload(
                  pick,
                  false,
                  `No ${missingValidColumns.join(', ')} ${pluralize(
                    'column',
                    missingValidColumns.length
                  )} ${pluralize('was', missingValidColumns.length)} found.`
                );
              }
            }
            return (
              <div className="CSVPreviewWidget-results">
                {this.renderResultsHeader(csv.name, parsedResults.parsedRowData.length, pick)}
                {this.renderResultsTable(parsedResults)}
              </div>
            );
          }

          const error = parseErrors && parseErrors.length > 0 ? parseErrors[0].message : undefined;
          return this.renderUpload(pick, loading, error);
        }}
      </CSVParser>
    );
  }
}

const getColumnHeaders = (columnNames: string[]): ColumnHeader[] => {
  return columnNames.map((name) => ({ name }));
};

const getColumnGenerators = (columnNames: string[]) => {
  return columnNames.map((name) => getTextCellGenerator(name));
};

export { CSVPreviewWidget };

export default DropTarget(
  NativeTypes.FILE,
  {
    drop: (_: Props, monitor, component: React.Component) => {
      const { files } = monitor.getItem();
      if (files.length > 1) {
        ToastActions.showErrorToast(
          'Oops! Looks like you tried to upload more than one file - please try again'
        );
        return;
      }

      const file = files[0];
      if (file) {
        component.setState({ csvFromDragDrop: file });
      }
    },
  },
  (connector, monitor) => ({
    connectDropTarget: connector.dropTarget(),
    isDraggingFile: monitor.isOver(),
  })
)(CSVPreviewWidget);
